import * as React from 'react'

/**
 * Rings the one thing the step asks for: a DOM control found by `selector`, or a
 * `rect` in viewport coordinates. Never touches the target, so it can point at
 * package DOM; a missing target draws nothing. The target moves, hence the
 * per-frame rect read. Web Animations API: Tailwind only applies inside
 * `.project-notes-scope`.
 */

const RING_PADDING = 7
const ACCENT = '#00b7ff'
// A breath, not a flash — the target can be a whole panel.
const BEACON_MS = 1800
// Fixed travel, not a scale factor: reads the same around a button and a 700px card.
const HALO_TRAVEL_PX = 28
// Dims everything but the target. Set to null for a ring with no dimming.
const BACKDROP = 'rgba(3, 10, 20, 0.55)'
// Must beat the viewport diagonal (≈1.42 vmax): with a circular hole the dimmed
// shape is itself a circle of this radius.
const BACKDROP_SPREAD = '200vmax'

// A covered control (a dialog opened over it) must not be ringed. Five sample points,
// so a toast across part of a wide panel doesn't count while a modal backdrop does.
const GUIDE_TOAST_SELECTOR = '[data-guide-toast]'

const isTargetOnTop = (target, rect) => {
    const left = Math.max(rect.left, 0)
    const top = Math.max(rect.top, 0)
    const right = Math.min(rect.left + rect.width, window.innerWidth)
    const bottom = Math.min(rect.top + rect.height, window.innerHeight)
    if (right <= left || bottom <= top) return false
    const width = right - left
    const height = bottom - top
    const samplePoints = [
        [0.5, 0.5],
        [0.25, 0.25],
        [0.75, 0.25],
        [0.25, 0.75],
        [0.75, 0.75],
    ]
    let coveredByOther = false
    for (const [fractionX, fractionY] of samplePoints) {
        const hit = document.elementFromPoint(
            left + width * fractionX,
            top + height * fractionY
        )
        // The guide's own toast collapses on demand; a modal doesn't.
        if (!hit || hit.closest(GUIDE_TOAST_SELECTOR)) continue
        if (target.contains(hit)) return true
        coveredByOther = true
    }
    return !coveredByOther
}

const readRect = selector => {
    // Several matches (one bookmark per AI message): the newest is the one meant.
    const matches = document.querySelectorAll(selector)
    const target = matches[matches.length - 1]
    if (!target) return null
    const rect = target.getBoundingClientRect()
    const { top, left, width, height } = rect
    if (width === 0 && height === 0) return null
    if (!isTargetOnTop(target, rect)) return null
    return { top, left, width, height }
}

const isSameRect = (a, b) =>
    a === b ||
    (!!a &&
        !!b &&
        Math.abs(a.top - b.top) < 0.5 &&
        Math.abs(a.left - b.left) < 0.5 &&
        Math.abs(a.width - b.width) < 0.5 &&
        Math.abs(a.height - b.height) < 0.5)

export default function Spotlight({
    selector,
    // Ringed instead when `selector` has no visible target — the control that
    // reveals it (a folded panel's open button on phones).
    fallbackSelector,
    rect: suppliedRect,
    round,
    testId,
    dim = true,
}) {
    const [trackedRect, setTrackedRect] = React.useState(null)
    const haloRef = React.useRef(null)
    const ringRef = React.useRef(null)

    React.useEffect(() => {
        if (!selector) {
            setTrackedRect(null)
            return
        }
        let frame = null
        const track = () => {
            const next =
                readRect(selector) ||
                (fallbackSelector ? readRect(fallbackSelector) : null)
            setTrackedRect(current =>
                isSameRect(current, next) ? current : next
            )
            frame = window.requestAnimationFrame(track)
        }
        track()
        return () => {
            if (frame !== null) window.cancelAnimationFrame(frame)
            setTrackedRect(null)
        }
    }, [selector, fallbackSelector])

    const rect = suppliedRect || trackedRect
    const hasRect = !!rect
    // Read at animation start only; the animation must not restart as the target moves.
    const frameSizeRef = React.useRef({ width: 1, height: 1 })
    if (rect) {
        frameSizeRef.current = {
            width: rect.width + RING_PADDING * 2,
            height: rect.height + RING_PADDING * 2,
        }
    }
    React.useEffect(() => {
        if (!hasRect) return
        const animations = []
        if (haloRef.current?.animate) {
            const { width, height } = frameSizeRef.current
            const scaleX = 1 + (HALO_TRAVEL_PX * 2) / width
            const scaleY = 1 + (HALO_TRAVEL_PX * 2) / height
            animations.push(
                haloRef.current.animate(
                    [
                        { transform: 'scale(1)', opacity: 0.9 },
                        {
                            transform: `scale(${scaleX}, ${scaleY})`,
                            opacity: 0,
                        },
                    ],
                    {
                        duration: BEACON_MS,
                        iterations: Infinity,
                        easing: 'cubic-bezier(0, 0, 0.2, 1)',
                    }
                )
            )
        }
        if (ringRef.current?.animate) {
            animations.push(
                ringRef.current.animate(
                    [{ opacity: 1 }, { opacity: 0.45 }, { opacity: 1 }],
                    { duration: BEACON_MS, iterations: Infinity }
                )
            )
        }
        return () => animations.forEach(animation => animation.cancel())
    }, [hasRect])

    if (!rect) return null

    const frame = {
        position: 'fixed',
        top: rect.top - RING_PADDING,
        left: rect.left - RING_PADDING,
        width: rect.width + RING_PADDING * 2,
        height: rect.height + RING_PADDING * 2,
        borderRadius: round ? '50%' : 12,
        // Above the AI widget's 1300 so targets inside it are still ringed.
        zIndex: 1310,
        pointerEvents: 'none',
    }

    return (
        <>
            {dim && BACKDROP ? (
                <div
                    aria-hidden="true"
                    data-testid={testId ? `${testId}-dim` : undefined}
                    style={{
                        ...frame,
                        // Under the toast (1320) so the instruction stays lit.
                        zIndex: 1305,
                        boxShadow: `0 0 0 ${BACKDROP_SPREAD} ${BACKDROP}`,
                    }}
                />
            ) : null}
            <div
                ref={haloRef}
                aria-hidden="true"
                style={{ ...frame, border: `3px solid ${ACCENT}` }}
            />
            <div
                ref={ringRef}
                aria-hidden="true"
                data-testid={testId}
                style={{
                    ...frame,
                    border: `3px solid ${ACCENT}`,
                    boxShadow: `0 0 0 2px rgba(4, 12, 24, 0.55), 0 0 0 6px rgba(0, 183, 255, 0.28), 0 0 22px 6px rgba(0, 183, 255, 0.6)`,
                }}
            />
        </>
    )
}
